import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  getSimulationSettings,
  saveSimulationSettings,
  previewSimulationScope,
  previewTransportSimulation,
  confirmSimulationArea,
} from '../../../api/transport-simulationApi';
import { getApiErrorMessage } from '../../../api/mix-deliveriesApi';
export const BUCKETS = {
  NOW: 'Do wykonania teraz',
  NEXT_SHIFT: 'Przygotowanie następnej zmiany',
  OVERDUE: 'Zaległy — potwierdź realizację',
  UNKNOWN: 'Sprawdź awizację',
};
const range = (w) =>
  `${w.start.replace('T', ' ')} – ${w.end.replace('T', ' ')}`;
export default function SimulationScopePanel({
  userRole,
  run,
  transports = [],
  onResult,
}) {
  const [settings, setSettings] = useState({
    hours: 4,
    includeNextShift: true,
  });
  const [defaults, setDefaults] = useState(null);
  const [window, setWindow] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [transport, setTransport] = useState('');
  const [area, setArea] = useState('AWH');
  const [clockTick, setClockTick] = useState(0);
  useEffect(() => {
    let active = true;
    getSimulationSettings()
      .then((s) => {
        if (active) {
          setDefaults(s);
          setSettings({ hours: s.hours, includeNextShift: s.includeNextShift });
        }
      })
      .catch((e) => {
        if (active) setError(getApiErrorMessage(e));
      });
    return () => {
      active = false;
    };
  }, []);
  useEffect(() => {
    const timer = setInterval(() => setClockTick((v) => v + 1), 60000);
    return () => clearInterval(timer);
  }, []);
  useEffect(() => {
    let active = true;
    previewSimulationScope(settings)
      .then((w) => {
        if (active) setWindow(w);
      })
      .catch((e) => {
        if (active) setError(getApiErrorMessage(e));
      });
    return () => {
      active = false;
    };
  }, [settings, clockTick]);
  useEffect(() => {
    setTransport('');
  }, [run?.id]);
  const execute = async (action) => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      await action();
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const selected = transports.find(
    (t) => `${t.transportNumber}/${t.loadingDate}` === transport,
  );
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Typography variant="h6">Zakres symulacji</Typography>
        {defaults && (
          <Typography>
            Ustawienia magazynu dla trybu bieżącego: {defaults.hours} godz.{' '}
            {defaults.includeNextShift
              ? '+ następna zmiana'
              : 'bez następnej zmiany'}
            .
          </Typography>
        )}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            select
            label="Horyzont ręcznego podglądu"
            value={settings.hours}
            disabled={busy}
            onChange={(e) =>
              setSettings((s) => ({ ...s, hours: Number(e.target.value) }))
            }
          >
            {[2, 4, 6, 8].map((h) => (
              <MenuItem key={h} value={h}>
                {h} godz.
              </MenuItem>
            ))}
          </TextField>
          <FormControlLabel
            control={
              <Switch
                checked={settings.includeNextShift}
                disabled={busy}
                onChange={(e) =>
                  setSettings((s) => ({
                    ...s,
                    includeNextShift: e.target.checked,
                  }))
                }
              />
            }
            label="Uwzględnij następną zmianę"
          />
        </Stack>
        {window && (
          <Typography>
            Podgląd zakresu (czas polski): {range(window)}.
            {settings.includeNextShift &&
              ` Następna zmiana: ${window.nextStart.replace(
                'T',
                ' ',
              )} – ${window.nextEnd.replace('T', ' ')}.`}{' '}
            Dokładne granice zostaną zapisane przy uruchomieniu.
          </Typography>
        )}
        <Typography>
          Ręczny podgląd nie zmienia bieżącego wyniku i nie wysyła maili.
          Zaległe transporty z pliku pozostają widoczne. Brak lub sprzeczna
          awizacja wymaga sprawdzenia i nie rezerwuje zapasu.
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            disabled={busy || !run}
            onClick={() =>
              execute(async () => {
                const result = await previewTransportSimulation(
                  run.transportImportId,
                  settings,
                );
                onResult(result);
              })
            }
          >
            Uruchom ręczny podgląd
          </Button>
          {userRole === 'ADMIN' && (
            <Button
              disabled={busy || !defaults}
              onClick={() =>
                execute(async () => {
                  const saved = await saveSimulationSettings(settings);
                  setDefaults(saved);
                  setMessage(
                    'Zapisano domyślny zakres magazynu. Obowiązuje przy następnej symulacji bieżącej.',
                  );
                })
              }
            >
              Zapisz jako domyślne dla magazynu
            </Button>
          )}
        </Stack>
        {run?.scope && (
          <Alert severity={run.mode === 'PREVIEW' ? 'info' : 'success'}>
            Wyświetlany wynik:{' '}
            {run.mode === 'PREVIEW'
              ? 'ręczny podgląd — bez powiadomień'
              : 'bieżąca symulacja'}
            . Zakres: {range(run.scope)}.
            {run.scope.includeNextShift &&
              ` Następna zmiana: ${run.scope.nextStart.replace(
                'T',
                ' ',
              )} – ${run.scope.nextEnd.replace('T', ' ')}.`}
          </Alert>
        )}
        {['ADMIN', 'KONTROLER'].includes(userRole) &&
          run?.mode !== 'PREVIEW' && (
            <>
              <Typography variant="subtitle1">
                Zatwierdzenie obszaru całego transportu
              </Typography>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                <TextField
                  select
                  label="Transport do zatwierdzenia"
                  value={transport}
                  sx={{ minWidth: 260 }}
                  onChange={(e) => {
                    setTransport(e.target.value);
                    const picked = transports.find(
                      (t) =>
                        `${t.transportNumber}/${t.loadingDate}` ===
                        e.target.value,
                    );
                    setArea(picked?.targetArea === 'TWH' ? 'TWH' : 'AWH');
                  }}
                >
                  <MenuItem value="">Wybierz transport</MenuItem>
                  {transports
                    .filter((t) => t.loadingDate)
                    .map((t) => (
                      <MenuItem
                        key={`${t.transportNumber}/${t.loadingDate}`}
                        value={`${t.transportNumber}/${t.loadingDate}`}
                      >
                        {t.transportNumber} · {t.targetArea ?? 'brak decyzji'}{' '}
                        {t.areaConfirmed ? '(zatwierdzony)' : ''}
                      </MenuItem>
                    ))}
                </TextField>
                <TextField
                  select
                  label="Obszar docelowy"
                  value={area}
                  onChange={(e) => setArea(e.target.value)}
                >
                  {['AWH', 'TWH'].map((a) => (
                    <MenuItem key={a} value={a}>
                      {a}
                    </MenuItem>
                  ))}
                </TextField>
                <Button
                  disabled={busy || !selected}
                  onClick={() =>
                    execute(async () => {
                      await confirmSimulationArea({
                        transportNumber: selected.transportNumber,
                        loadingDate: selected.loadingDate,
                        targetArea: area,
                      });
                      setMessage(
                        'Zatwierdzono obszar. Przelicz bieżące transporty, aby odświeżyć zalecenia.',
                      );
                    })
                  }
                >
                  Zatwierdź obszar
                </Button>
                <Button
                  disabled={busy || !selected}
                  onClick={() =>
                    execute(async () => {
                      await confirmSimulationArea({
                        transportNumber: selected.transportNumber,
                        loadingDate: selected.loadingDate,
                      });
                      setMessage(
                        'Usunięto zatwierdzenie. Przelicz transporty, aby ponownie wybrać obszar.',
                      );
                    })
                  }
                >
                  Przywróć automatyczny wybór
                </Button>
              </Stack>
            </>
          )}
        {error && <Alert severity="error">{error}</Alert>}
        {message && <Alert severity="success">{message}</Alert>}
      </Stack>
    </Paper>
  );
}
